import type { BoardSummary, TaskColor } from '@obras/shared';
import { Check } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useOrgMembers } from '../usuarios/api.js';
import { ColorPicker } from './ColorPicker.js';
import { PeoplePicker, type Person } from './PeoplePicker.js';
import { useAddBoardMember, useCreateBoard, useRemoveBoardMember, useUpdateBoard } from './api.js';

/**
 * Quadro, na criação e na edição.
 *
 * Na criação as colunas também são texto, uma por linha — mesmo gesto do
 * checklist, para não haver duas formas de montar uma lista nesta tela. Na
 * edição as colunas somem daqui: mexer nelas é no próprio quadro, onde se vê o
 * que cada uma tem dentro.
 */
export function BoardFormModal({
  open,
  onClose,
  board,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Ausente é criação. */
  board?: BoardSummary | undefined;
  onCreated?: ((boardId: string) => void) | undefined;
}) {
  const [name, setName] = useState(board?.name ?? '');
  const [description, setDescription] = useState(board?.description ?? '');
  const [color, setColor] = useState<TaskColor | null>(board?.color ?? null);
  const [columns, setColumns] = useState('A fazer\nEm andamento\nConcluído');
  const [memberIds, setMemberIds] = useState<string[]>(
    (board?.members ?? []).map((person) => person.id),
  );
  const [formError, setFormError] = useState<string | null>(null);

  const create = useCreateBoard();
  const update = useUpdateBoard();
  const addMember = useAddBoardMember();
  const removeMember = useRemoveBoardMember();
  const members = useOrgMembers();
  const { user } = useAuth();

  const saving = create.isPending || update.isPending;

  const people: Person[] = (members.data?.items ?? []).map((member) => ({
    id: member.id,
    name: member.name,
    detail: member.jobTitle ?? member.email ?? undefined,
  }));

  /* Num quadro que já existe, o assento entra e sai na hora. */
  function toggleMember(id: string, next: boolean): void {
    setMemberIds((current) => (next ? [...current, id] : current.filter((item) => item !== id)));

    if (!board) return;

    const action = next
      ? addMember.mutateAsync({ boardId: board.id, userId: id })
      : removeMember.mutateAsync({ boardId: board.id, userId: id });

    void action.catch((error: unknown) => {
      setFormError(error instanceof ApiError ? error.message : 'Não foi possível alterar.');
      // Desfaz na tela o que o servidor recusou.
      setMemberIds((current) => (next ? current.filter((item) => item !== id) : [...current, id]));
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    try {
      if (board) {
        await update.mutateAsync({
          id: board.id,
          name: name.trim(),
          description: description.trim() || undefined,
          color,
        });
      } else {
        const names = columns
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line !== '');

        const created = await create.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          ...(color ? { color } : {}),
          ...(names.length > 0 ? { columns: names } : {}),
          memberIds,
        });

        onCreated?.(created.id);
        setName('');
        setDescription('');
        setMemberIds([]);
      }

      onClose();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Não foi possível salvar o quadro.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={board ? 'Editar quadro' : 'Novo quadro'}
      description="Quem for designado a uma tarefa deste quadro passa a ter acesso a ele."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="board-form"
            type="submit"
            loading={saving}
            icon={<Check size={16} strokeWidth={1.5} />}
          >
            {board ? 'Salvar' : 'Criar quadro'}
          </Button>
        </>
      }
    >
      <form
        id="board-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        {formError ? (
          <div role="alert" className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger">
            {formError}
          </div>
        ) : null}

        <TextField
          label="Nome"
          required
          placeholder="Fiscalização da Av. Osmar Demeneck"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <TextField
          label="Descrição"
          placeholder="Opcional"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        <ColorPicker value={color} onChange={setColor} />

        {board ? null : (
          <TextAreaField
            label="Colunas"
            rows={4}
            hint="Uma por linha, na ordem em que aparecem."
            value={columns}
            onChange={(event) => setColumns(event.target.value)}
          />
        )}

        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Membros</span>
          <PeoplePicker
            people={people}
            selected={memberIds}
            onToggle={toggleMember}
            /* Quem administra o quadro e quem o criou; nao tira o proprio assento. */
            lockedIds={board && user ? [user.id] : []}
            emptyLabel={members.isLoading ? 'Carregando…' : 'Ninguém encontrado.'}
          />
          <p className="text-[13px] text-muted-foreground">
            Os membros têm acesso ao quadro independentemente de estarem designados a alguma tarefa.
          </p>
        </div>
      </form>
    </Modal>
  );
}
